import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'
import { orderBy } from 'lodash'
import { insertMessagesInItems } from 'lib/messagesLib'

import useSnackbar from '../Snackbar/useSnackbar'

const COMMON_ERROR_FETCHDATA_LABEL = 'common.error.fetchData'
const useCarsToBuy = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    cars: [],
    total: 0,
    error: null,
  })

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error,
      }))
    },
    [setState]
  )

  const setStartUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: false,
      isUpdating: true,
    }))
  }, [setState])

  const setStopUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isUpdating: false,
    }))
  }, [setState])

  const setSuccessFetching = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        isUpdating: false,
        ...data,
        error: null,
      }))
    },
    [setState]
  )

  const fetchCarsToBuy = useCallback(
    async (sessionId, partnerId) => {
      setStartFetching()
      try {
        const results = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/cars-to-buy`
        )

        const cars = orderBy(results.data.body.items, ['title'], ['asc'])

        const messagesResult = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/messages`
        )

        const messages = messagesResult.data.body.messages.filter(
          (message) => message.partnerId === partnerId
        )

        insertMessagesInItems(messages, cars)

        setSuccessFetching({
          cars,
          total: results.data.body.total,
        })
      } catch (e) {
        let message = t(COMMON_ERROR_FETCHDATA_LABEL, {
          attribute: e.response?.status,
        })

        setStopFetching(e)

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartFetching,
      setStopFetching,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  const updateChoice = useCallback(
    async (sessionId, partnerId, vin, choice, reprice) => {
      setStartUpdating()

      try {
        const result = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/cars-to-buy/${vin}`,
          { choice, reprice }
        )
        const {
          data: { body: response },
        } = result
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId)
        return response.success
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t(COMMON_ERROR_FETCHDATA_LABEL, {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
        return null
      }
    },
    [fetchCarsToBuy, setStartUpdating, setStopUpdating, showSnackbarMessage, t]
  )

  const addMessage = useCallback(
    async (sessionId, partnerId, vin, message) => {
      setStartUpdating()

      try {
        await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/messages/${vin}`,
          { message }
        )
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId)
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t(COMMON_ERROR_FETCHDATA_LABEL, {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [fetchCarsToBuy, setStartUpdating, setStopUpdating, showSnackbarMessage, t]
  )

  return {
    cars: state.cars,
    total: state.total,
    isFetching: state.isFetching,
    isUpdating: state.isUpdating,
    error: state.error,
    fetchCarsToBuy,
    updateChoice,
    addMessage,
  }
}

export default useCarsToBuy
